import * as React from 'react';

import { Redirect } from 'react-router-dom';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import { Divider } from '../../components/Divider';
import { HorizontalBarChart } from '../../components/HorizontalBarChart';
import {
  FullMatchFragment,
  useMatchVoteCountsQuery,
  PlayerVoteCountFragmentFragment,
} from '../../generated/graphql';
import { useTitle } from '../../useTitle';
import { Heading } from '../../components/Heading';
import { BackLink } from '../../components/BackLink';
import { paths } from '../../paths';
import { LoadingSpinner } from '../../components/LoadingContainer';
import { isNil } from '../../utils/isNil';
import { PlayerNameWithAvatar } from '../Match/components/PlayerNameWithAvatar';
import { useAuth } from '../../contexts/AuthContext';
import { getEqNode } from '../../utils/comparison';
import { byVoteType } from '../../utils/sorting';
import { getVoteLabel } from './voteLabelLookup';

interface Props {
  match: FullMatchFragment;
}

export interface PlayerVotes {
  name: string;
  votes: number;
}

const voteRankToDataPoint =
  (isSignedIn: boolean) =>
  ({ player, voteCount }: PlayerVoteCountFragmentFragment) => ({
    label: () => (
      <div className="flex items-center">
        <PlayerNameWithAvatar player={player} linkToPlayer={isSignedIn} />
      </div>
    ),
    value: voteCount,
  });

const voteBarColors = ['bg-amber-500', 'bg-primary'];

const getBarColor = (idx: number) =>
  pipe(voteBarColors, A.lookup(idx % voteBarColors.length), O.toUndefined);

export const Summary: React.FC<Props> = ({ match }) => {
  useTitle(`Vote Summary | Match vs ${match.opponent}`);
  const { user } = useAuth();
  const [matchVoteCountRes] = useMatchVoteCountsQuery({
    variables: { matchId: match.id },
  });

  const voteCounts = matchVoteCountRes.data?.match?.voteResult?.counts;

  if (
    match.votingStatus !== 'CLOSED' ||
    (!matchVoteCountRes.fetching && isNil(voteCounts))
  ) {
    return <Redirect to={paths.match.home(match.id)} />;
  }

  if (isNil(voteCounts)) {
    return <LoadingSpinner className="border-primary-500 mx-auto" />;
  }

  const voteTypes = pipe(
    voteCounts,
    A.map(({ voteType }) => voteType),
    A.uniq(getEqNode()),
    A.sort(byVoteType),
  );

  return (
    <div className="space-y-4">
      <BackLink to={paths.match.votes.read(match.id)}>Back to votes</BackLink>
      <Heading level={1} className="text-2xl">
        Vote Summary
      </Heading>

      {pipe(
        voteTypes,
        A.filterMapWithIndex((idx, voteType) => {
          const counts = voteCounts.filter(
            (count) => count.voteType.id === voteType.id,
          );
          if (A.isEmpty(counts)) {
            return O.none;
          }
          const voteData = counts.map(voteRankToDataPoint(!isNil(user)));

          const totalVotesOfType = counts.reduce(
            (total, c) => total + c.voteCount,
            0,
          );

          const barColorClass = getBarColor(idx);

          return O.some(
            <div key={voteType.id} className="space-y-4">
              <h3 className="text-lg">
                {getVoteLabel(voteType.name)} votes ({totalVotesOfType})
              </h3>
              {A.isEmpty(voteData) ? (
                <p>Not enough data</p>
              ) : (
                <HorizontalBarChart
                  data={voteData}
                  barColorClass={barColorClass}
                />
              )}
              <Divider />
            </div>,
          );
        }),
      )}
    </div>
  );
};
